import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { AlertTriangle, CalendarPlus, CalendarClock, Loader2, MapPin, Phone, Video, Mail, MessageCircle } from 'lucide-react';
import { interviewsV2, errorText, toIstParts, fromIstParts, fmtIst, MODE_LABEL } from './interviewApi';

/**
 * ScheduleInterviewDialog - one dialog for schedule AND reschedule (Interviews v2).
 *
 * Props
 *   open, onOpenChange           - controlled visibility
 *   candidate                    - { id, name|full_name, email, phone }   (schedule mode)
 *   requirementId, clientId      - optional context (pipeline / requirement page)
 *   interview                    - an existing v2 interview -> reschedule mode
 *   onSaved(result)              - called with the API response after a successful save
 *
 * Used from the candidate page, the requirement pipeline and the Interviews page.
 */
const ROUNDS = ['Interview', 'Screening call', 'Technical round', 'HR round', 'Managerial round', 'Final round', 'Client discussion'];
const DURATIONS = [30, 45, 60, 90];

const emptyForm = () => ({
  round_name: 'Interview', mode: 'video', date: '', time: '', duration_minutes: 45, location: '', meeting_link: '',
  interviewer_ids: [], interviewer_extra: '', client_contact_id: '', notes: '', send_email: true, send_whatsapp: true,
  requirement_id: '',
});

export default function ScheduleInterviewDialog({ open, onOpenChange, candidate, requirementId, clientId, interview, onSaved }) {
  const isReschedule = !!interview;
  const [form, setForm] = useState(emptyForm());
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [contacts, setContacts] = useState({ client_name: null, contacts: [] });
  const [status, setStatus] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [allowConflict, setAllowConflict] = useState(false);

  const candidateName = candidate?.full_name || candidate?.name || interview?.candidate_name || 'Candidate';
  const candidateId = candidate?.id || interview?.candidate_id;

  // reset + preload every time the dialog opens
  useEffect(() => {
    if (!open) return;
    const f = emptyForm();
    f.requirement_id = requirementId || interview?.requirement_id || '';
    if (interview) {
      const p = toIstParts(interview.scheduled_at);
      Object.assign(f, {
        round_name: interview.round_name || 'Interview', mode: interview.mode || 'video', date: p.date, time: p.time,
        duration_minutes: interview.duration_minutes || 45, location: interview.location || '', meeting_link: interview.meeting_link || '',
        interviewer_ids: interview.interviewer_ids || [], client_contact_id: interview.client_contact_id || '', notes: interview.notes || '',
      });
    }
    setForm(f);
    setReason('');
    setConflicts([]);
    setAllowConflict(false);
    interviewsV2.users().then(setUsers).catch(() => setUsers([]));
    interviewsV2.status().then(setStatus).catch(() => setStatus(null));
    if (!interview && !requirementId) {
      interviewsV2.requirements({ limit: 200 }).then((r) => setRequirements(r || [])).catch(() => setRequirements([]));
    }
  }, [open, interview, requirementId]);

  // client contacts follow the requirement / client
  useEffect(() => {
    if (!open) return;
    const params = clientId ? { client_id: clientId } : form.requirement_id ? { requirement_id: form.requirement_id } : null;
    if (!params) { setContacts({ client_name: null, contacts: [] }); return; }
    interviewsV2.contacts(params).then((c) => {
      setContacts(c || { client_name: null, contacts: [] });
      if (!form.client_contact_id && c && c.contacts && c.contacts.length && !interview) {
        const primary = c.contacts.find((x) => x.is_primary) || c.contacts[0];
        setForm((prev) => ({ ...prev, client_contact_id: primary.id }));
      }
    }).catch(() => setContacts({ client_name: null, contacts: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientId, form.requirement_id]);

  const scheduledAt = useMemo(() => fromIstParts(form.date, form.time), [form.date, form.time]);
  const selectedContact = contacts.contacts.find((c) => c.id === form.client_contact_id);

  // live conflict check
  useEffect(() => {
    if (!open || !scheduledAt) { setConflicts([]); return; }
    const t = setTimeout(() => {
      interviewsV2.conflicts({
        scheduled_at: scheduledAt, duration_minutes: form.duration_minutes, candidate_id: candidateId,
        interviewer_ids: form.interviewer_ids.join(','), client_contact_email: selectedContact?.email || '',
        exclude: interview?.id || '',
      }).then((r) => setConflicts(r.conflicts || [])).catch(() => setConflicts([]));
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scheduledAt, form.duration_minutes, form.interviewer_ids, form.client_contact_id]);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const toggleInterviewer = (id) => set('interviewer_ids', form.interviewer_ids.includes(id) ? form.interviewer_ids.filter((x) => x !== id) : [...form.interviewer_ids, id]);

  const validate = () => {
    if (!form.date || !form.time) return 'Pick a date and a time';
    if (!scheduledAt || new Date(scheduledAt) < new Date(Date.now() - 5 * 60000)) return 'That time is in the past';
    if (form.mode === 'in_person' && !form.location.trim()) return 'Venue / address is required for an in-person interview';
    if (form.mode === 'video' && !form.meeting_link.trim()) return 'Meeting link is required for a video interview';
    if (conflicts.length && !allowConflict) return 'This slot clashes with another interview - tick "schedule anyway" to proceed';
    return null;
  };

  const submit = async () => {
    const problem = validate();
    if (problem) { toast.error(problem); return; }
    setSaving(true);
    try {
      let result;
      if (isReschedule) {
        result = await interviewsV2.reschedule(interview.id, {
          scheduled_at: scheduledAt, duration_minutes: Number(form.duration_minutes), mode: form.mode,
          location: form.location.trim() || null, meeting_link: form.meeting_link.trim() || null, reason: reason.trim() || null,
          send_email: form.send_email, send_whatsapp: form.send_whatsapp, allow_conflict: allowConflict,
        });
        toast.success(`Rescheduled to ${fmtIst(result.interview.scheduled_at)} - invites updated (SEQUENCE ${result.interview.ics_sequence})`);
      } else {
        result = await interviewsV2.create({
          candidate_id: candidateId, requirement_id: form.requirement_id || requirementId || null, client_id: clientId || null,
          round_name: form.round_name.trim() || 'Interview', mode: form.mode, scheduled_at: scheduledAt,
          duration_minutes: Number(form.duration_minutes), location: form.location.trim() || null, meeting_link: form.meeting_link.trim() || null,
          interviewer_ids: form.interviewer_ids,
          interviewer_names: form.interviewer_extra.split(',').map((s) => s.trim()).filter(Boolean),
          client_contact_id: form.client_contact_id && form.client_contact_id !== 'none' ? form.client_contact_id : null,
          notes: form.notes.trim() || null, send_email: form.send_email, send_whatsapp: form.send_whatsapp, allow_conflict: allowConflict,
        });
        toast.success(`Interview scheduled for ${fmtIst(result.interview.scheduled_at)}`);
      }
      const em = result.email || {};
      if (em.status === 'skipped') toast.warning(`Invite emails not sent: ${em.reason}`);
      else if (em.results) {
        const failed = Object.entries(em.results).filter(([, v]) => v === 'failed').map(([k]) => k);
        const missing = Object.entries(em.results).filter(([, v]) => v === 'no_email').map(([k]) => k);
        if (failed.length) toast.error(`Email failed for: ${failed.join(', ')}`);
        if (missing.length) toast.info(`No email on file for: ${missing.join(', ')} - .ics not sent to them`);
      }
      const wa = result.whatsapp || {};
      const refused = Object.entries(wa).filter(([, v]) => v && v.status === 'refused_unapproved');
      if (refused.length) toast.warning('WhatsApp not sent - the interview templates are not owner-approved yet (Settings > WhatsApp centre)');
      if (result.google_calendar && result.google_calendar.status === 'ics_only') toast.info('Google Calendar not connected - .ics invites sent instead');
      else if (result.google_calendar && result.google_calendar.status === 'failed') toast.warning(`Google Calendar sync failed: ${result.google_calendar.reason || ''}`);
      onOpenChange(false);
      if (onSaved) onSaved(result);
    } catch (e) {
      if (e.status === 409 && e.data && e.data.detail && e.data.detail.conflicts) {
        setConflicts(e.data.detail.conflicts);
        toast.error('This slot clashes with another interview');
      } else {
        toast.error(errorText(e.message, 'Could not save the interview'));
      }
    } finally {
      setSaving(false);
    }
  };

  const ModeIcon = form.mode === 'in_person' ? MapPin : form.mode === 'phone' ? Phone : Video;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="schedule-interview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isReschedule ? <CalendarClock className="w-5 h-5 text-amber-600" /> : <CalendarPlus className="w-5 h-5 text-emerald-600" />}
            {isReschedule ? 'Reschedule interview' : 'Schedule interview'} - {candidateName}
          </DialogTitle>
          <DialogDescription>
            {isReschedule
              ? `Currently ${fmtIst(interview.scheduled_at)}. Everyone gets an updated calendar invite; pending reminders are re-planned.`
              : 'Candidate, client contact and interviewers receive an email with a calendar (.ics) invite. Reminders go out automatically.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
          {!isReschedule && !requirementId && (
            <div className="md:col-span-2">
              <Label>Requirement / position</Label>
              <Select value={form.requirement_id || 'none'} onValueChange={(v) => set('requirement_id', v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Pick the requirement (sets the client + contacts)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No requirement (general interview)</SelectItem>
                  {requirements.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.position_title || r.title || 'Requirement'}{r.client_name ? ` - ${r.client_name}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label>Round</Label>
            <Input list="iv2-rounds" value={form.round_name} onChange={(e) => set('round_name', e.target.value)} placeholder="Technical round" data-testid="iv2-round" />
            <datalist id="iv2-rounds">{ROUNDS.map((r) => <option key={r} value={r} />)}</datalist>
          </div>
          <div>
            <Label>Mode</Label>
            <Select value={form.mode} onValueChange={(v) => set('mode', v)}>
              <SelectTrigger data-testid="iv2-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="video">Video meeting</SelectItem>
                <SelectItem value="in_person">In person</SelectItem>
                <SelectItem value="phone">Phone call</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Date (IST)</Label>
            <Input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} data-testid="iv2-date" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Time (IST)</Label>
              <Input type="time" value={form.time} onChange={(e) => set('time', e.target.value)} data-testid="iv2-time" />
            </div>
            <div>
              <Label>Duration</Label>
              <Select value={String(form.duration_minutes)} onValueChange={(v) => set('duration_minutes', Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DURATIONS.map((d) => <SelectItem key={d} value={String(d)}>{d} min</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="md:col-span-2">
            <Label className="flex items-center gap-1"><ModeIcon className="w-3.5 h-3.5" />
              {form.mode === 'in_person' ? 'Venue / address' : form.mode === 'video' ? 'Meeting link' : 'Phone note (optional)'}
            </Label>
            {form.mode === 'in_person' ? (
              <Textarea rows={2} value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="Full address the candidate should reach" data-testid="iv2-location" />
            ) : form.mode === 'video' ? (
              <Input value={form.meeting_link} onChange={(e) => set('meeting_link', e.target.value)} placeholder="https://meet.google.com/..." data-testid="iv2-link" />
            ) : (
              <Input value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="e.g. interviewer will call the candidate" />
            )}
          </div>

          {!isReschedule && (
            <>
              <div>
                <Label>Client contact {contacts.client_name ? `(${contacts.client_name})` : ''}</Label>
                <Select value={form.client_contact_id || 'none'} onValueChange={(v) => set('client_contact_id', v)}>
                  <SelectTrigger data-testid="iv2-contact"><SelectValue placeholder="Who at the client?" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No client contact</SelectItem>
                    {contacts.contacts.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}{c.designation ? ` - ${c.designation}` : ''}{c.email ? '' : ' (no email)'}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!contacts.contacts.length && (form.requirement_id || clientId) && (
                  <p className="text-xs text-slate-500 mt-1">No contacts on this client yet - add them in Clients &gt; Contacts.</p>
                )}
              </div>
              <div>
                <Label>Interviewers (our side)</Label>
                <div className="border rounded-md p-2 max-h-28 overflow-y-auto space-y-1 bg-white">
                  {users.length === 0 && <p className="text-xs text-slate-400">No users loaded</p>}
                  {users.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={form.interviewer_ids.includes(u.id)} onCheckedChange={() => toggleInterviewer(u.id)} />
                      <span>{u.full_name}</span>
                    </label>
                  ))}
                </div>
                <Input className="mt-1" value={form.interviewer_extra} onChange={(e) => set('interviewer_extra', e.target.value)} placeholder="Client-side interviewer names, comma separated" />
              </div>
              <div className="md:col-span-2">
                <Label>Notes for the candidate</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Documents to carry, who to ask for, dress code..." />
              </div>
            </>
          )}

          {isReschedule && (
            <div className="md:col-span-2">
              <Label>Reason (goes into the emails)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Client travelling / candidate asked / panel unavailable" />
            </div>
          )}

          {conflicts.length > 0 && (
            <div className="md:col-span-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="iv2-conflicts">
              <div className="flex items-center gap-2 font-medium text-amber-800"><AlertTriangle className="w-4 h-4" /> Clashes with {conflicts.length} scheduled interview{conflicts.length > 1 ? 's' : ''}</div>
              <ul className="mt-1 ml-6 list-disc text-amber-900">
                {conflicts.map((c) => (
                  <li key={c.id}>{c.candidate_name}{c.client_name ? ` @ ${c.client_name}` : ''} - {fmtIst(c.scheduled_at)} ({(c.with || []).join(', ')})</li>
                ))}
              </ul>
              <label className="mt-2 flex items-center gap-2 cursor-pointer">
                <Checkbox checked={allowConflict} onCheckedChange={(v) => setAllowConflict(!!v)} /> Schedule anyway
              </label>
            </div>
          )}

          <div className="md:col-span-2 flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={form.send_email} onCheckedChange={(v) => set('send_email', !!v)} />
              <Mail className="w-4 h-4 text-slate-500" /> Email + .ics invite
              {status && status.smtp_configured === false && <span className="text-xs text-red-600">(SMTP not configured)</span>}
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={form.send_whatsapp} onCheckedChange={(v) => set('send_whatsapp', !!v)} />
              <MessageCircle className="w-4 h-4 text-slate-500" /> WhatsApp
              {status && status.whatsapp_approved && !status.whatsapp_approved.dcs_interview_candidate && (
                <span className="text-xs text-amber-700">(template awaiting approval - will be refused)</span>
              )}
            </label>
            {status && (
              <span className="text-xs text-slate-500 self-center">
                Calendar: {status.google_calendar === 'connected' ? `Google (${status.google_calendar_email || 'connected'}) + .ics` : '.ics only (Google Calendar not connected)'}
              </span>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="iv2-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {isReschedule ? 'Reschedule & re-send invites' : 'Schedule & send invites'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { MODE_LABEL };
